import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Copy, Check, Send, ArrowLeft, ClipboardPaste, RefreshCw, ShieldCheck, AlertTriangle, Clock, Loader2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { getBackendErrorMessage } from '../utils/toastError';
import useEscapeKey from '../hooks/useEscapeKey';

const BuyFiresModal = ({ isOpen, onClose, onSuccess, onBack }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [paymentMethod, setPaymentMethod] = useState('bs'); // 'bs' | 'crypto'
  const [cryptoNetwork, setCryptoNetwork] = useState('usdt_tron'); // 'usdt_tron' | 'usdt_bsc'
  const [formData, setFormData] = useState({
    amount: '',
    bank_reference: '',
    usdt_amount: '',
    tx_hash: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [copiedBank, setCopiedBank] = useState(false);
  const [copiedWallet, setCopiedWallet] = useState(false);
  const [fiatContext, setFiatContext] = useState(null);
  const [inputMode, setInputMode] = useState('fires'); // 'fires' | 'bs'

  // On-chain verification states
  const [txStatus, setTxStatus] = useState('idle'); // 'idle' | 'submitted' | 'verifying' | 'verified_auto' | 'verified_manual' | 'failed'
  const [submittedRequest, setSubmittedRequest] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  const handleClose = useCallback(() => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    setPaymentMethod('bs');
    setCryptoNetwork('usdt_tron');
    setInputMode('fires');
    setFormData({ amount: '', bank_reference: '', usdt_amount: '', tx_hash: '', bs_amount: '' });
    setErrors({});
    setTxStatus('idle');
    setSubmittedRequest(null);
    setVerificationData(null);
    onClose();
  }, [onClose, pollingInterval]);

  useEscapeKey(handleClose, isOpen);

  const bankData = `0102 Venezuela
20827955
0412-225.00.16
Pago`;

  React.useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const fetchFiatContext = async () => {
      try {
        const res = await axios.get('/api/economy/fiat-context');
        if (!cancelled) {
          setFiatContext(res.data);
        }
      } catch (error) {
        console.error('Error fetching FIAT context', error);
      }
    };

    fetchFiatContext();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Auto-detect tx hash from clipboard when switching to crypto tab
  useEffect(() => {
    if (!isOpen || paymentMethod !== 'crypto') return;

    const detectHash = async () => {
      try {
        if (!navigator.clipboard || !navigator.clipboard.readText) return;
        const text = await navigator.clipboard.readText();
        if (!text || text.length < 20) return;

        const hash = extractTxHash(text, cryptoNetwork);
        if (hash && !formData.tx_hash) {
          setFormData(prev => ({ ...prev, tx_hash: hash }));
          toast.success('Hash detectado automáticamente desde el portapapeles');
        }
      } catch {
        // Silencioso: clipboard puede no estar disponible en todos los navegadores
      }
    };

    detectHash();
  }, [isOpen, paymentMethod, cryptoNetwork]);

  // Cleanup polling on close
  useEffect(() => {
    if (!isOpen && pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  }, [isOpen, pollingInterval]);

  const extractTxHash = (text, network) => {
    if (!text) return null;
    text = text.trim();

    if (network === 'usdt_bsc') {
      // BSC/ETH hash: 0x + 64 hex chars
      const match = text.match(/0x[a-fA-F0-9]{64}/);
      if (match) return match[0];
    } else {
      // TRON hash: 64 hex chars (without 0x prefix usually)
      const match = text.match(/[a-fA-F0-9]{64}/);
      if (match) return match[0];
    }
    return null;
  };

  const checkVerificationStatus = useCallback(async (hash) => {
    if (!hash) return;
    try {
      const res = await axios.get(`/api/economy/verify-tx/${hash}`);
      const data = res.data;
      setVerificationData(data);

      if (data.verification_status === 'auto_approved') {
        setTxStatus('verified_auto');
        toast.success(`¡${data.amount} fuegos acreditados automáticamente!`);
        queryClient.invalidateQueries(['fire-requests']);
        queryClient.invalidateQueries(['wallet-transactions']);
        queryClient.invalidateQueries(['user-stats']);
        onSuccess && onSuccess({ request: data });
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
      } else if (data.verification_status === 'verified') {
        setTxStatus('verified_manual');
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
      } else if (data.verification_status === 'verification_failed') {
        setTxStatus('failed');
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }
      }
      // else: still pending_verification or null — keep polling
    } catch (err) {
      // Silencioso en polling
    }
  }, [cryptoNetwork, onSuccess, pollingInterval, queryClient]);

  const startPolling = useCallback((hash) => {
    // Immediate check
    checkVerificationStatus(hash);
    // Then every 10s
    const interval = setInterval(() => checkVerificationStatus(hash), 10000);
    setPollingInterval(interval);
  }, [checkVerificationStatus]);

  const handleVerifyNow = async () => {
    if (!submittedRequest?.tx_hash) return;
    setTxStatus('verifying');
    await checkVerificationStatus(submittedRequest.tx_hash);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Para bank_reference, solo permitir números
    if (name === 'bank_reference') {
      const numericValue = value.replace(/\D/g, '');
      setFormData(prev => ({ ...prev, [name]: numericValue }));
    } else if (name === 'amount' || name === 'bs_amount') {
      const cleanValue = value.replace(/[^0-9.]/g, '');
      const parts = cleanValue.split('.');
      const normalized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : cleanValue;

      if (inputMode === 'bs' && name === 'bs_amount') {
        // User typed Bs -> compute fires
        const rate = parseFloat(fiatContext?.operationalRate?.rate);
        const firesPerUsdt = fiatContext?.config?.fires_per_usdt || 100;
        if (Number.isFinite(rate) && rate > 0 && normalized) {
          const bs = parseFloat(normalized);
          const usdt = bs / rate;
          const fires = usdt * firesPerUsdt;
          setFormData(prev => ({ ...prev, amount: String(Math.round(fires)), bs_amount: normalized }));
        } else {
          setFormData(prev => ({ ...prev, bs_amount: normalized }));
        }
      } else if (inputMode === 'fires' && name === 'amount') {
        setFormData(prev => ({ ...prev, amount: normalized }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name] || errors['amount'] || errors['bs_amount']) {
      setErrors(prev => ({ ...prev, [name]: null, amount: null, bs_amount: null }));
    }
  };

  const handlePaste = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.readText) {
        const text = await navigator.clipboard.readText();
        const numericValue = text.replace(/\D/g, '');
        setFormData(prev => ({ ...prev, bank_reference: numericValue }));
        toast.success('Referencia pegada');
        if (errors.bank_reference) {
          setErrors(prev => ({ ...prev, bank_reference: null }));
        }
      } else {
        toast.error('Tu navegador no permite pegar desde el portapapeles');
      }
    } catch (error) {
      toast.error('No se pudo acceder al portapapeles');
    }
  };

  const handleCopyBankData = async () => {
    try {
      await navigator.clipboard.writeText(bankData);
      setCopiedBank(true);
      toast.success('Datos bancarios copiados');
      setTimeout(() => setCopiedBank(false), 2000);
    } catch (error) {
      toast.error('Error al copiar');
    }
  };

  const validate = () => {
    const newErrors = {};

    if (paymentMethod === 'bs') {
      if (!formData.amount) {
        newErrors.amount = 'Cantidad requerida';
      } else {
        const amount = parseFloat(formData.amount);
        if (isNaN(amount) || amount <= 0) {
          newErrors.amount = 'Cantidad inválida';
        }
      }

      if (!formData.bank_reference) {
        newErrors.bank_reference = 'Referencia bancaria requerida';
      } else if (formData.bank_reference.length < 4) {
        newErrors.bank_reference = 'Referencia muy corta';
      }
    } else {
      if (!formData.usdt_amount) {
        newErrors.usdt_amount = 'Monto USD requerido';
      } else {
        const usdt = parseFloat(formData.usdt_amount);
        if (isNaN(usdt) || usdt <= 0) {
          newErrors.usdt_amount = 'Monto USD inválido';
        }
      }

      if (!formData.tx_hash) {
        newErrors.tx_hash = 'Hash de transacción requerido';
      } else if (formData.tx_hash.length < 10) {
        newErrors.tx_hash = 'Hash demasiado corto';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      let payload;

      if (paymentMethod === 'bs') {
        payload = {
          amount: formData.amount,
          bank_reference: formData.bank_reference,
          payment_method: 'bs'
        };
      } else {
        payload = {
          payment_method: cryptoNetwork,
          usdt_amount: formData.usdt_amount,
          tx_hash: formData.tx_hash,
          network: cryptoNetwork === 'usdt_bsc' ? 'BSC' : (fiatContext?.config?.usdt_network || 'TRON')
        };
      }

      const response = await axios.post('/api/economy/request-fires', payload);

      if (paymentMethod === 'crypto') {
        // Crypto: stay in modal and start polling
        setSubmittedRequest(response.data?.request || null);
        setTxStatus('submitted');
        startPolling(formData.tx_hash);
        toast.success('Solicitud enviada. Verificando transacción en la blockchain...');
      } else {
        // Bs: close as usual
        toast.success('Solicitud enviada. Será revisada por un administrador.');
        queryClient.invalidateQueries(['fire-requests']);
        onSuccess && onSuccess(response.data);
        handleClose();
      }
    } catch (error) {
      toast.error(getBackendErrorMessage(error, 'Error al enviar solicitud'));
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const firesPerUsdt = fiatContext?.config?.fires_per_usdt || 100;
  const usdtWallet = fiatContext?.config?.usdt_official_wallet || 'TLNsU1jj17zvRHbrJW27Xv9DGK9KiQBD3y';
  const usdtNetwork = fiatContext?.config?.usdt_network || 'TRON';

  // Conversion helpers
  const rate = fiatContext?.operationalRate ? parseFloat(fiatContext.operationalRate.rate) : null;
  const validRate = Number.isFinite(rate) && rate > 0;

  const computeFiresFromBs = (bsStr) => {
    if (!validRate || !bsStr) return null;
    const bs = parseFloat(bsStr);
    if (!Number.isFinite(bs) || bs <= 0) return null;
    const usdt = bs / rate;
    return Math.round(usdt * firesPerUsdt);
  };

  const computeBsFromFires = (firesStr) => {
    if (!validRate || !firesStr) return null;
    const fires = parseFloat(firesStr);
    if (!Number.isFinite(fires) || fires <= 0) return null;
    const usdt = fires / firesPerUsdt;
    return usdt * rate;
  };

  const previewFires = inputMode === 'bs' ? computeFiresFromBs(formData.bs_amount) : parseFloat(formData.amount || 0);
  const previewBs = inputMode === 'fires' ? computeBsFromFires(formData.amount) : parseFloat(formData.bs_amount || 0);

  return (
    <>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[1600] flex items-center justify-center overflow-y-auto p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="w-full max-w-md max-h-[92vh] my-auto card-glass p-4 sm:p-6 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (onBack) {
                      onBack();
                    } else {
                      handleClose();
                      navigate('/profile?tab=fires');
                    }
                  }}
                  className="p-2 hover:bg-glass-hover rounded-lg transition-colors"
                >
                  <ArrowLeft size={20} />
                </button>
                <div className="w-10 h-10 rounded-full bg-fire-orange/20 flex items-center justify-center">
                  <ShoppingCart size={20} className="text-fire-orange" />
                </div>
                <h2 className="text-xl font-bold">Comprar Fuegos</h2>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-glass-hover rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="space-y-4">
              {/* Payment method selector */}
              <div className="flex bg-surface-1 border border-theme rounded-xl p-1.5 text-sm">
                <button
                  type="button"
                  onClick={() => { setPaymentMethod('bs'); setErrors({}); }}
                  className={`flex-1 py-2.5 rounded-lg transition-all font-bold ${
                    paymentMethod === 'bs'
                      ? 'bg-[#FEF7E0] text-[#B06000] border border-[#FEEFC3] shadow-sm'
                      : 'bg-transparent text-text-secondary hover:text-text hover:bg-surface-2'
                  }`}
                >
                  Pago en Bs
                </button>
                <button
                  type="button"
                  onClick={() => { setPaymentMethod('crypto'); setErrors({}); }}
                  className={`flex-1 py-2.5 rounded-lg transition-all font-bold ml-1 ${
                    paymentMethod === 'crypto'
                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200 shadow-sm'
                      : 'bg-transparent text-text-secondary hover:text-text hover:bg-surface-2'
                  }`}
                >
                  Pago en USD
                </button>
              </div>

              {/* Crypto network selector */}
              {paymentMethod === 'crypto' && (
                <div className="flex bg-surface-1 border border-theme rounded-xl p-1 text-xs">
                  <button
                    type="button"
                    onClick={() => { setCryptoNetwork('usdt_tron'); setFormData(prev => ({ ...prev, tx_hash: '' })); setErrors({}); }}
                    className={`flex-1 py-2 rounded-lg transition-all font-bold ${
                      cryptoNetwork === 'usdt_tron'
                        ? 'bg-emerald-50 text-emerald-800 border border-emerald-200 shadow-sm'
                        : 'bg-transparent text-text-secondary hover:text-text hover:bg-surface-2'
                    }`}
                  >
                    TRON (TRC20)
                  </button>
                  <button
                    type="button"
                    onClick={() => { setCryptoNetwork('usdt_bsc'); setFormData(prev => ({ ...prev, tx_hash: '' })); setErrors({}); }}
                    className={`flex-1 py-2 rounded-lg transition-all font-bold ml-1 ${
                      cryptoNetwork === 'usdt_bsc'
                        ? 'bg-amber-50 text-amber-900 border border-amber-200 shadow-sm'
                        : 'bg-transparent text-text-secondary hover:text-text hover:bg-surface-2'
                    }`}
                  >
                    BSC (BEP20)
                  </button>
                </div>
              )}

              {paymentMethod === 'bs' ? (
                <>
                  {/* Instructions Bs */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <p className="text-xs text-blue-900 leading-relaxed">
                      💡 <strong>Pasos:</strong>
                      <br />1. Realiza el pago a los datos bancarios
                      <br />2. Ingresa la cantidad de fuegos y tu referencia
                      <br />3. Espera la aprobación del administrador
                    </p>
                  </div>

                  {/* Bank Data */}
                  <div className="p-4 rounded-xl bg-surface-1 border border-theme space-y-3">
                    <label className="text-xs font-bold text-text-secondary uppercase tracking-wider block">
                      Datos Bancarios para Pago
                    </label>
                    <div className="bg-card border border-theme rounded-lg p-3">
                      <pre className="text-sm text-primary font-mono font-bold whitespace-pre-wrap">
                        {bankData}
                      </pre>
                    </div>
                    <button
                      onClick={handleCopyBankData}
                      className="w-full btn-secondary text-xs py-2.5 flex items-center justify-center gap-2"
                    >
                      {copiedBank ? (
                        <>
                          <Check size={16} className="text-emerald-600" />
                          <span className="text-emerald-700 font-bold">¡Copiado!</span>
                        </>
                      ) : (
                        <>
                          <Copy size={16} />
                          Copiar Datos Bancarios
                        </>
                      )}
                    </button>
                  </div>

                  {/* Input mode toggle */}
                  <div className="flex bg-surface-1 border border-theme rounded-xl p-1 text-xs">
                    <button
                      type="button"
                      onClick={() => setInputMode('fires')}
                      className={`flex-1 py-2 rounded-lg transition-all font-bold ${
                        inputMode === 'fires'
                          ? 'bg-[#FCE8E6] text-[#C5221F] border border-[#FAD2CF] shadow-sm'
                          : 'bg-transparent text-text-secondary hover:text-text hover:bg-surface-2'
                      }`}
                    >
                      Escribir en Fuegos 🔥
                    </button>
                    <button
                      type="button"
                      onClick={() => setInputMode('bs')}
                      className={`flex-1 py-2 rounded-lg transition-all font-bold ml-1 ${
                        inputMode === 'bs'
                          ? 'bg-[#FEF7E0] text-[#B06000] border border-[#FEEFC3] shadow-sm'
                          : 'bg-transparent text-text-secondary hover:text-text hover:bg-surface-2'
                      }`}
                    >
                      Escribir en Bs 💵
                    </button>
                  </div>

                  {/* Amount Input (bidirectional) */}
                  <div>
                    <label className="block text-sm font-medium text-text/80 mb-2">
                      {inputMode === 'bs' ? 'Monto en Bolívares (Bs)' : 'Cantidad de Fuegos a Solicitar'}
                    </label>
                    <input
                      type="text"
                      name={inputMode === 'bs' ? 'bs_amount' : 'amount'}
                      value={inputMode === 'bs' ? formData.bs_amount : formData.amount}
                      onChange={handleChange}
                      className={`input-glass w-full ${errors.amount ? 'border-red-500' : ''}`}
                      placeholder={inputMode === 'bs' ? 'Ej: 50000' : 'Ej: 100'}
                      inputMode="decimal"
                    />
                    {errors.amount && (
                      <p className="text-xs text-red-400 mt-1">{errors.amount}</p>
                    )}
                    {inputMode === 'bs' && validRate && previewFires > 0 && (
                      <p className="text-xs text-text/40 mt-1">
                        Recibirás aproximadamente {previewFires} 🔥
                        {rate && ` (tasa: ${rate.toFixed(2)} Bs/USD)`}
                      </p>
                    )}
                    {inputMode === 'bs' && (!validRate || !formData.bs_amount) && (
                      <p className="text-xs text-text/40 mt-1">
                        El equivalente en fuegos se calcula con la tasa MundoXYZ vigente.
                      </p>
                    )}
                    {inputMode === 'fires' && validRate && previewBs > 0 && (
                      <p className="text-xs text-text/40 mt-1">
                        Deposita: {previewBs.toFixed(2)} Bs (1🔥 = {(previewBs / previewFires).toFixed(2)} Bs)
                      </p>
                    )}
                    {inputMode === 'fires' && (!validRate || !formData.amount) && (
                      <p className="text-xs text-text/40 mt-1">
                        El valor en Bs se calcula con la tasa MundoXYZ vigente al registrar tu solicitud.
                      </p>
                    )}
                  </div>

                  {/* Bank Reference Input */}
                  <div>
                    <label className="block text-sm font-medium text-text/80 mb-2">
                      Referencia Bancaria (solo números)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="bank_reference"
                        value={formData.bank_reference}
                        onChange={handleChange}
                        className={`input-glass w-full ${errors.bank_reference ? 'border-red-500' : ''}`}
                        placeholder="123456789"
                        inputMode="numeric"
                      />
                      <button
                        type="button"
                        onClick={handlePaste}
                        className="px-3 py-2 bg-glass hover:bg-glass-hover rounded-lg transition-colors flex items-center justify-center gap-1 text-xs whitespace-nowrap"
                        title="Pegar desde portapapeles"
                      >
                        <ClipboardPaste size={14} />
                        Pegar
                      </button>
                    </div>
                    {errors.bank_reference && (
                      <p className="text-xs text-red-400 mt-1">{errors.bank_reference}</p>
                    )}
                    <p className="text-xs text-text/40 mt-1">
                      Ingresa solo los números de tu comprobante de pago
                    </p>
                  </div>
                </>
              ) : (
                <>
                  {/* Instructions Crypto */}
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                    <p className="text-xs text-emerald-900 leading-relaxed">
                      💡 <strong>Pasos:</strong>
                      <br />1. Envía USDT {cryptoNetwork === 'usdt_bsc' ? '(BSC/BEP20)' : '(TRON/TRC20)'} a la billetera oficial
                      <br />2. Ingresa el monto en USDT y el hash de la transacción
                      <br />3. El sistema verifica automáticamente en la blockchain
                      <br />4. Si coinciden los datos, recibes tus fuegos al instante (≤{fiatContext?.config?.usdt_auto_approve_max || 10} USDT)
                    </p>
                  </div>

                  {/* Dynamic wallet per network */}
                  <div className="p-4 rounded-xl bg-surface-1 border border-theme space-y-3">
                    <label className="text-xs font-bold text-text-secondary uppercase tracking-wider block">
                      Billetera oficial {cryptoNetwork === 'usdt_bsc' ? 'BSC (BEP20)' : 'TRON (TRC20)'}
                    </label>
                    <div className="flex justify-center py-2">
                      <div className="bg-white p-2.5 rounded-xl border border-theme shadow-sm">
                        <QRCodeSVG
                          value={cryptoNetwork === 'usdt_bsc' ? (fiatContext?.config?.usdt_wallet_bsc || usdtWallet) : usdtWallet}
                          size={140}
                          level="M"
                          includeMargin={false}
                        />
                      </div>
                    </div>
                    <div className="bg-card border border-theme rounded-lg p-3 overflow-x-auto">
                      <div className="text-xs sm:text-sm text-emerald-700 font-mono font-bold break-all">
                        {cryptoNetwork === 'usdt_bsc' ? (fiatContext?.config?.usdt_wallet_bsc || usdtWallet) : usdtWallet}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const wallet = cryptoNetwork === 'usdt_bsc' ? (fiatContext?.config?.usdt_wallet_bsc || usdtWallet) : usdtWallet;
                          await navigator.clipboard.writeText(wallet);
                          setCopiedWallet(true);
                          toast.success('Billetera copiada');
                          setTimeout(() => setCopiedWallet(false), 2000);
                        } catch (err) {
                          toast.error('No se pudo copiar la billetera');
                        }
                      }}
                      className="w-full btn-secondary text-xs py-2.5 flex items-center justify-center gap-2"
                    >
                      {copiedWallet ? (
                        <>
                          <Check size={16} className="text-emerald-600" />
                          <span className="text-emerald-700 font-bold">¡Copiado!</span>
                        </>
                      ) : (
                        <>
                          <Copy size={16} />
                          Copiar Billetera {cryptoNetwork === 'usdt_bsc' ? 'BSC' : 'TRON'}
                        </>
                      )}
                    </button>
                  </div>

                  {/* Estimated confirmation time hint */}
                  <div className="bg-surface-2 border border-theme rounded-lg p-2.5 flex items-center gap-2">
                    <Clock size={14} className="text-text-secondary shrink-0" />
                    <p className="text-xs text-text-secondary">
                      Tiempo estimado de confirmación: {cryptoNetwork === 'usdt_bsc' ? '~3 min (15 bloques BSC)' : '~1 min (19 bloques TRON)'}
                    </p>
                  </div>

                  {/* USDT Amount Input */}
                  <div>
                    <label className="block text-sm font-medium text-text/80 mb-2">
                      Monto a enviar en USDT
                    </label>
                    <input
                      type="number"
                      name="usdt_amount"
                      value={formData.usdt_amount}
                      onChange={handleChange}
                      className={`input-glass w-full ${errors.usdt_amount ? 'border-red-500' : ''}`}
                      placeholder="Ej: 1.00"
                      min="0.01"
                      step="0.01"
                    />
                    {errors.usdt_amount && (
                      <p className="text-xs text-red-400 mt-1">{errors.usdt_amount}</p>
                    )}
                    {formData.usdt_amount && !errors.usdt_amount && (
                      <p className="text-xs text-text-secondary mt-1">
                        Recibirás aproximadamente <strong className="text-text font-bold">{(parseFloat(formData.usdt_amount || 0) * firesPerUsdt).toFixed(2)} 🔥</strong>
                      </p>
                    )}
                  </div>

                  {/* Tx Hash Input */}
                  <div>
                    <label className="block text-sm font-medium text-text/80 mb-2">
                      Hash de Transacción
                    </label>
                    <input
                      type="text"
                      name="tx_hash"
                      value={formData.tx_hash}
                      onChange={handleChange}
                      className={`input-glass w-full ${errors.tx_hash ? 'border-red-500' : ''}`}
                      placeholder={cryptoNetwork === 'usdt_bsc' ? '0x...' : 'a1b2c3d4...'}
                    />
                    {errors.tx_hash && (
                      <p className="text-xs text-red-400 mt-1">{errors.tx_hash}</p>
                    )}
                    <p className="text-xs text-text-secondary mt-1">
                      Pega el hash de tu transacción {cryptoNetwork === 'usdt_bsc' ? 'BSC (0x...)' : 'TRON (64 caracteres hex)'}. Lo detectamos automáticamente si está en tu portapapeles.
                    </p>
                  </div>

                  {/* Pre-send summary card */}
                  {formData.usdt_amount && formData.tx_hash && !errors.usdt_amount && !errors.tx_hash && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3.5 space-y-1.5">
                      <p className="text-xs font-bold text-emerald-900 mb-1">Resumen de tu solicitud</p>
                      <div className="flex justify-between text-xs text-emerald-900">
                        <span>🔥 Fuegos a recibir:</span>
                        <span className="font-mono font-bold">{(parseFloat(formData.usdt_amount || 0) * firesPerUsdt).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-emerald-900">
                        <span>💵 Envías:</span>
                        <span className="font-mono font-bold">{parseFloat(formData.usdt_amount).toFixed(2)} USDT</span>
                      </div>
                      <div className="flex justify-between text-xs text-emerald-900">
                        <span>⛓️ Red:</span>
                        <span className="font-mono font-bold">{cryptoNetwork === 'usdt_bsc' ? 'BSC (BEP20)' : 'TRON (TRC20)'}</span>
                      </div>
                      <div className="flex justify-between text-xs text-emerald-900">
                        <span>🎯 A:</span>
                        <span className="font-mono font-bold truncate max-w-[120px]">
                          {cryptoNetwork === 'usdt_bsc' ? (fiatContext?.config?.usdt_wallet_bsc || usdtWallet) : usdtWallet}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-emerald-900">
                        <span>#️⃣ Hash:</span>
                        <span className="font-mono font-bold truncate max-w-[120px]">{formData.tx_hash}</span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Post-submit verification states (crypto only) */}
              {paymentMethod === 'crypto' && txStatus !== 'idle' && (
                <div className="space-y-3 pt-2">
                  {txStatus === 'submitted' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Loader2 size={18} className="text-primary animate-spin" />
                        <p className="text-sm font-bold text-blue-900">Verificando en la blockchain...</p>
                      </div>
                      <p className="text-xs text-blue-800">
                        Esperando confirmaciones de red. Esto puede tardar {cryptoNetwork === 'usdt_bsc' ? '~3 min' : '~1 min'}.
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={handleVerifyNow}
                          className="flex-1 py-2 px-3 btn-primary text-xs flex items-center justify-center gap-1"
                        >
                          <RefreshCw size={14} />
                          Verificar ahora
                        </button>
                        <button
                          onClick={handleClose}
                          className="py-2 px-3 btn-secondary text-xs"
                        >
                          Cerrar y seguir
                        </button>
                      </div>
                    </div>
                  )}

                  {txStatus === 'verifying' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
                      <Loader2 size={20} className="text-primary animate-spin" />
                      <p className="text-sm font-bold text-blue-900">Consultando transacción...</p>
                    </div>
                  )}

                  {txStatus === 'verified_auto' && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <ShieldCheck size={20} className="text-emerald-700" />
                        <p className="text-sm font-bold text-emerald-900">¡Pago verificado y aprobado!</p>
                      </div>
                      <p className="text-xs text-emerald-800">
                        Tus {submittedRequest?.amount || verificationData?.amount || ''} fuegos ya fueron acreditados a tu cuenta.
                      </p>
                      <button
                        onClick={handleClose}
                        className="w-full btn-primary text-sm font-medium"
                      >
                        ¡Listo!
                      </button>
                    </div>
                  )}

                  {txStatus === 'verified_manual' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Clock size={18} className="text-amber-700" />
                        <p className="text-sm font-bold text-amber-900">Pago verificado en blockchain</p>
                      </div>
                      <p className="text-xs text-amber-800">
                        Tu transacción fue confirmada correctamente. Como supera el umbral de auto-aprobación, un administrador la aprobará en breve.
                      </p>
                      <button
                        onClick={handleClose}
                        className="w-full btn-secondary text-sm"
                      >
                        Entendido
                      </button>
                    </div>
                  )}

                  {txStatus === 'failed' && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <AlertTriangle size={18} className="text-red-600" />
                        <p className="text-sm font-bold text-red-900">Verificación fallida</p>
                      </div>
                      <p className="text-xs text-red-800">
                        {verificationData?.verification_data?.error || 'No se pudo verificar la transacción. Revisa que el hash y el monto sean correctos, o contacta soporte.'}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={handleVerifyNow}
                          className="flex-1 btn-secondary text-xs"
                        >
                          Reintentar verificación
                        </button>
                        <button
                          onClick={handleClose}
                          className="flex-1 py-2 bg-red-100 hover:bg-red-200 text-red-800 rounded-lg transition-colors text-xs font-semibold"
                        >
                          Cerrar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Submit Buttons */}
              {txStatus === 'idle' && (
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleClose}
                    className="flex-1 btn-secondary"
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {loading ? (
                      'Enviando...'
                    ) : (
                      <>
                        <Send size={18} />
                        {paymentMethod === 'crypto' ? 'Verificar y Enviar' : 'Enviar Solicitud'}
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Warning */}
              {txStatus === 'idle' && (
                <div className="bg-warning/10 border border-warning/30 rounded-lg p-3">
                  <p className="text-xs text-warning">
                    {paymentMethod === 'crypto'
                      ? '⚠️ Asegúrate de enviar exactamente el monto y red indicados. El sistema verifica automáticamente.'
                      : '⚠️ Tu solicitud será revisada por un administrador. Asegúrate de que la referencia sea correcta.'}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>

  </>
  );
};

export default BuyFiresModal;
